import { useEffect } from 'react'
import { BrandConfig } from '../config/brands.config'

const upsertHeadTag = <T extends HTMLElement>(selector: string, create: () => T): T => {
  const existing = document.head.querySelector<T>(selector)
  if (existing) return existing

  const created = create()
  document.head.appendChild(created)
  return created
}

const setMetaContent = (selector: string, attr: 'name' | 'property', key: string, content: string) => {
  const tag = upsertHeadTag<HTMLMetaElement>(selector, () => {
    const meta = document.createElement('meta')
    meta.setAttribute(attr, key)
    return meta
  })
  tag.setAttribute('content', content)
}

export function useBrandDocumentMeta(config: BrandConfig) {
  const { meta } = config

  useEffect(() => {
    if (!meta) return

    document.title = meta.title

    if (meta.description) {
      setMetaContent('meta[name="description"]', 'name', 'description', meta.description)
      setMetaContent('meta[property="og:description"]', 'property', 'og:description', meta.description)
    }

    setMetaContent('meta[property="og:title"]', 'property', 'og:title', meta.title)
    setMetaContent('meta[property="og:site_name"]', 'property', 'og:site_name', config.name)

    if (meta.themeColor) {
      setMetaContent('meta[name="theme-color"]', 'name', 'theme-color', meta.themeColor)
    }

    if (meta.favicon) {
      const icon = upsertHeadTag<HTMLLinkElement>('link[rel="shortcut icon"], link[rel="icon"]', () => {
        const link = document.createElement('link')
        link.rel = 'icon'
        return link
      })
      icon.type = 'image/png'
      icon.href = meta.favicon

      const appleIcon = upsertHeadTag<HTMLLinkElement>('link[rel="apple-touch-icon"]', () => {
        const link = document.createElement('link')
        link.rel = 'apple-touch-icon'
        return link
      })
      appleIcon.href = meta.favicon
    }
  }, [meta, config.name])
}
